import { cognitoClientId } from '@/shared/constants';
import { KnownAccount } from './types';

const KNOWN_ACCOUNTS_KEY = 'c360:knownAccounts';
const COGNITO_PREFIX = `CognitoIdentityServiceProvider.${cognitoClientId}`;

function readAccounts(): KnownAccount[] {
  try {
    const raw = localStorage.getItem(KNOWN_ACCOUNTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeAccounts(accounts: KnownAccount[]): void {
  localStorage.setItem(KNOWN_ACCOUNTS_KEY, JSON.stringify(accounts));
}

export function getKnownAccounts(): KnownAccount[] {
  return readAccounts().sort((a, b) => b.lastLoginAt - a.lastLoginAt);
}

export function addOrUpdateAccount(partial: Partial<KnownAccount> & { email: string }): void {
  const accounts = readAccounts();
  const idx = accounts.findIndex(
    (a) => a.email.toLowerCase() === partial.email.toLowerCase(),
  );

  if (idx >= 0) {
    accounts[idx] = { ...accounts[idx], ...partial };
  } else {
    accounts.push({
      remembered: true,
      lastLoginAt: Date.now(),
      ...partial,
    });
  }

  writeAccounts(accounts);
}

export function removeAccount(email: string): void {
  // Clear that user's Cognito tokens from both storages. Before dropping the
  // registry entry: it holds the Cognito username the tokens are keyed by.
  clearCognitoTokensForUser(email, localStorage);
  clearCognitoTokensForUser(email, sessionStorage);

  const accounts = readAccounts().filter(
    (a) => a.email.toLowerCase() !== email.toLowerCase(),
  );
  writeAccounts(accounts);
}

export function markAccountAsNotRemembered(email: string): void {
  const accounts = readAccounts();
  const idx = accounts.findIndex(
    (a) => a.email.toLowerCase() === email.toLowerCase(),
  );

  if (idx >= 0) {
    accounts[idx].remembered = false;
    writeAccounts(accounts);
  }

  // Tokens are left alone: signOut() already decides their fate, keeping them
  // for "Remember me" and clearing them otherwise. Clearing here undid that.
}

export function hasRememberedSession(email: string): boolean {
  // Check by email
  const refreshTokenKey = `${COGNITO_PREFIX}.${email}.refreshToken`;
  if (localStorage.getItem(refreshTokenKey) !== null) return true;

  // Also check by Cognito username (UUID) since getUsername() returns the sub, not the email
  const account = readAccounts().find(
    (a) => a.email.toLowerCase() === email.toLowerCase(),
  );
  if (account?.cognitoUsername) {
    const refreshTokenKeyBySub = `${COGNITO_PREFIX}.${account.cognitoUsername}.refreshToken`;
    if (localStorage.getItem(refreshTokenKeyBySub) !== null) return true;
  }

  return false;
}

export function getCognitoUsername(email: string): string | undefined {
  const account = readAccounts().find(
    (a) => a.email.toLowerCase() === email.toLowerCase(),
  );
  return account?.cognitoUsername;
}

function clearCognitoTokensForUser(email: string, storage: Storage): void {
  // The SDK keys tokens by Cognito username, which is the sub (UUID), not the
  // email. Clearing by email alone left the session intact, and the next page
  // load restored it through LastAuthUser -- the user was signed straight back in.
  const usernames = [email, getCognitoUsername(email)].filter((u): u is string => !!u);
  const userPrefixes = usernames.map((u) => `${COGNITO_PREFIX}.${u}.`);
  const keysToRemove: string[] = [];

  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && userPrefixes.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }

  const lastAuthUserKey = `${COGNITO_PREFIX}.LastAuthUser`;
  if (usernames.includes(storage.getItem(lastAuthUserKey) ?? '')) {
    keysToRemove.push(lastAuthUserKey);
  }

  keysToRemove.forEach((key) => storage.removeItem(key));
}
